// Interpolazione dei valori configurati in un flow.
//
// Convenzione allineata a `TriggerActionExecutor::resolveValue()` su api-dev:
// i placeholder sono `{{path}}`, più le keyword `{{now}}`, `{{today}}`,
// `{{timestamp}}`.
//
// DIFFERENZA VOLUTA rispetto al PHP: se la stringa è composta ESATTAMENTE da un
// solo placeholder, viene restituito il valore TIPATO invece della sua
// rappresentazione testuale. Serve perché lato frontend `"{{answers.ore}}"`
// deve poter arrivare come number a un `set_field`, non come "12".

import type { FlowScope } from './flowTypes'
import { getScopeValue } from './scope'

// Il trattino è ammesso nei segmenti: gli id dei nodi sono in kebab-case
// (`passo-1`, `officina-accettazione`), quindi un path come
// `answers.officina-accettazione.campo` deve essere riconosciuto. Senza il
// trattino nella classe il placeholder non veniva sostituito e il valore
// risultava vuoto senza un solo errore.
const PLACEHOLDER = /\{\{\s*([$\w][\w.$-]*)\s*\}\}/g
const SINGLE_PLACEHOLDER = /^\{\{\s*([$\w][\w.$-]*)\s*\}\}$/

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

export function formatDateOnly(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function formatDateTime(d: Date): string {
  return `${formatDateOnly(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/**
 * Keyword temporali disponibili nei template, con l'esempio di cosa producono.
 *
 * Elenco esportato perché il BUILDER lo mostra all'admin: se le keyword vivono
 * solo dentro lo switch, chi configura un flow non può sapere che esistono — e
 * infatti finisce per cercare `now()`, che non esiste.
 *
 * I formati non sono scelti qui a caso: sono ESATTAMENTE quelli che
 * `CDatePicker` salva nella risposta (vedi il suo `fromEvent` in
 * `controls.ts`), `{{today}}` per il campo data e `{{now}}` per il campo con
 * `timepicker`. Un valore iniziale scritto con queste keyword entra quindi nel
 * controllo senza conversioni.
 */
export const TEMPLATE_KEYWORDS: Array<{ name: string; description: string }> = [
  { name: 'now', description: 'Data e ora correnti — AAAA-MM-GG hh:mm:ss (campo data con orario)' },
  { name: 'today', description: 'Data odierna — AAAA-MM-GG (campo data)' },
  { name: 'timestamp', description: 'Secondi dal 1970 (numero)' },
]

/** Keyword temporali, valutate al momento della risoluzione. */
function resolveKeyword(name: string): unknown {
  switch (name) {
    case 'now': return formatDateTime(new Date())
    case 'today': return formatDateOnly(new Date())
    case 'timestamp': return Math.floor(Date.now() / 1000)
    default: return undefined
  }
}

/**
 * `{{now()}}` non è la sintassi giusta e il regex dei placeholder non lo
 * riconosce nemmeno: resterebbe nel testo come stringa letterale, e chi l'ha
 * scritto vedrebbe "{{now()}}" salvato in un campo data senza un solo errore.
 * È l'errore che viene naturale fare, quindi vale un avviso esplicito.
 */
const CHIAMATA_DI_FUNZIONE = /\{\{\s*(\w+)\(\)\s*\}\}/

function avvisaSeChiamataDiFunzione(value: string): void {
  const match = CHIAMATA_DI_FUNZIONE.exec(value)
  if (!match) return
  console.warn(
    `[flow/template] '${match[0]}' non è un placeholder valido e resterà nel testo così com'è. `
    + `Le keyword si scrivono senza parentesi: {{${match[1]}}}.`,
  )
}

function lookup(path: string, scope: FlowScope): unknown {
  const keyword = resolveKeyword(path)
  if (keyword !== undefined) return keyword
  return getScopeValue(scope, path)
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return String(value)
}

/**
 * Risolve un singolo valore.
 * - non-stringa                  → restituito com'è
 * - `"{{path}}"` (solo quello)   → valore tipato dallo scope
 * - stringa con placeholder      → interpolazione testuale
 */
export function resolveTemplate(value: unknown, scope: FlowScope): unknown {
  if (typeof value !== 'string') return value

  const exact = SINGLE_PLACEHOLDER.exec(value)
  if (exact) return lookup(exact[1], scope)

  if (!value.includes('{{')) return value

  avvisaSeChiamataDiFunzione(value)

  return value.replace(PLACEHOLDER, (_match, path: string) => stringify(lookup(path, scope)))
}

/** Applica `resolveTemplate` in profondità su array e oggetti semplici. */
export function resolveDeep<T>(value: T, scope: FlowScope): T {
  if (Array.isArray(value)) {
    return value.map((v) => resolveDeep(v, scope)) as unknown as T
  }
  if (value !== null && typeof value === 'object') {
    // Solo oggetti "dati": Date, ref e istanze di classe passano intatti.
    if (value instanceof Date) return value
    const proto = Object.getPrototypeOf(value)
    if (proto !== Object.prototype && proto !== null) return value

    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = resolveDeep(v, scope)
    }
    return out as unknown as T
  }
  return resolveTemplate(value, scope) as T
}
